import { AnnotationWhereGroup } from "components/annotations/AnnotationWhereGroup";
import { ColumnInformationDto } from "components/annotations/interface";
import { VariableDto } from "components/annotations/VariableDto";
import {
  AioExternalReplacements,
  AioExternalSingle,
  AioReplacement,
} from "../../additional/interface";
import { MosaicTrafficLightsValue } from "../../traffic-lights/MosaicTrafficLightsValue";
import { EditorDefns } from "../../types/interface";
import { AitCellData } from "./AitCellData";
import { AitRowData } from "./AitRowData";
import { AitRowGroupData } from "./AitRowGroupData";
import { GridItemTrafficLight } from "./GridItemTrafficLight";

/**
 * Configuration for TLF view modes.
 *
 * @param value - The unique identifier for this view mode
 * @param label - The display label for this view mode
 * @param hideForFigures - Optional. When true, hides this mode for figure TLFs
 * @param noRepeatProcessing - Optional. When true, disables repeat processing for this mode
 */
export interface TlfViewModeConfig {
  value: string;
  label: string;
  hideForFigures?: boolean;
  noRepeatProcessing?: boolean;
}

/**
 * Available TLF view mode configurations.
 */
export const TLF_VIEW_MODES: TlfViewModeConfig[] = [
  // The first value will be used as the default
  { value: "TLF", label: "TLF view" },
  { value: "ANNOTATIONS", label: "Annotations view" },
  {
    value: "SUPPRESS_REPEATS",
    label: "Suppress repeats",
    hideForFigures: true,
    noRepeatProcessing: true,
  },
];

/**
 * Text justification values for a cell.
 */
export type AitCellJustifyText = "left" | "center" | "right" | "decimal" | "default";

/**
 * Header cell data extending the base cell data with span information.
 *
 * @template T The data type used for cell text and comments.
 */
export interface AitHeaderCellData<T> extends AitCellData<T> {
  /** Number of columns this header cell spans. */
  colSpan: number;
  /** Number of rows this header cell spans. */
  rowSpan: number;
  /** Column span after any replacements or repeats. */
  repeatColSpan?: number;
  /** Row span after any replacements or repeats. */
  repeatRowSpan?: number;
  /** Number of space-after spans crossed by this header cell. */
  spaceAfterSpan?: number;
}

/**
 * Grouping for table rows or columns that may contain subgroups and replacements.
 *
 * @template T The data type used for replaceable text values.
 */
export interface AitTableGroup<T> {
  /** Unique identifier for the group. */
  aitid: string;
  /** Optional annotation or variable associated with the group. */
  annotation?: AnnotationWhereGroup | VariableDto;
  /** The type of group (row or column). */
  groupType: AitTableGroupType;
  /** Optional display name for the group. */
  name?: string;
  /** Text within the group that can be replaced. */
  replacableText: T;
  /** Optional replacement lists used by the group. */
  replacements?: AioReplacement<T>[];
  /** Nested subgroup entries. */
  subGroups: AitTableGroup<T>[];
  /** Equivalent AIT id when this group originates from a template. */
  templateAitId?: string;
}

/**
 * Header row data
 */
export interface AitHeaderRowData<T> extends AitRowData<T> {
  /** Header cells for this row. */
  cells: AitHeaderCellData<T>[];
}

/**
 * Header group data
 */
export interface AitHeaderGroupData<T> extends AitRowGroupData<T> {
  /** Unique identifier for the header group. */
  aitid: string;
  /** Header rows for this group. */
  rows: AitHeaderRowData<T>[];
}

/**
 * Full table data including header and body groups.
 *
 * @template T The data type used for comments and cell text.
 */
export interface AitTableData<T> {
  /** Header group data or false when no header exists. */
  headerData: AitHeaderGroupData<T> | false;
  /** Body groups that make up the table body. */
  bodyData: AitRowGroupData<T>[];
  /** Optional global comments for the table. */
  comments?: T;
  /** Number of label columns before the data columns begin. */
  rowHeaderColumns?: number;
  /** Percentage used to align decimal points. */
  decimalAlignPercent?: number;
  /** Optional figure string (for legacy v1FigureBody use). */
  figureString?: string;
  /** Column-level metadata and annotations. */
  columnInformation: ColumnInformationDto[];
  /** Traffic light value for the table's core status. */
  coreTrafficLight: MosaicTrafficLightsValue;
  /** Groups that have changed. */
  changedRowGroups?: GridItemTrafficLight[];
  /** Columns that have changed. */
  changedColumns?: GridItemTrafficLight[];
  /** Optional global annotation for the table. */
  tableAnnotation?: AnnotationWhereGroup;
}

/**
 * Table data after repeat processing
 */
export interface AitProcessedTableData<T> extends AitTableData<T> {
  /** Optional column repeat metadata. */
  columnRepeats?: AitColumnRepeat[] | null;
}

export type AitTableGroupType = "rowGroup" | "columnGroup";

export enum AitTablePosition {
  table = "table",
  header = "header",
  headerRow = "headerRow",
  headerCell = "headerCell",
  body = "body",
  bodyRowGroup = "bodyRowGroup",
  bodyRow = "bodyRow",
  bodyCell = "bodyCell",
  column = "column",
}

/**
 * Base interface for all location types within the table.
 */
export interface AitLocationBase {
  /** The position within the table structure. */
  position: AitTablePosition;
}

/**
 * Table level location
 */
export interface AitLocationTable extends AitLocationBase {
  /** Location referring to the whole table. */
  position: AitTablePosition.table;
}

/**
 * Header section location
 */
export interface AitLocationHeader extends AitLocationBase {
  /** Location referring to a header section. */
  position: AitTablePosition.header;
  /** The row type for the referenced table section. */
  tableSection: AitRowType;
  /** Row group index (zero for header). */
  rowGroup: 0;
  /** Identifier for the row group. */
  rowGroupId: string;
}

/**
 * Header row location
 */
export interface AitLocationHeaderRow extends AitLocationBase {
  /** Location referring to a header row. */
  position: AitTablePosition.headerRow;
  /** This location is in the header section. */
  tableSection: AitRowType.header;
  /** Header row group index. */
  rowGroup: 0;
  /** Identifier for the header row group. */
  rowGroupId: string;
  /** Row index within the header. */
  row: number;
  /** Row identifier. */
  rowId: string;
  /** Header rows do not have repeats. */
  rowRepeat: undefined;
  /** Header rows are never repeats. */
  isRepeat: false;
}

/**
 * Header cell location
 */
export interface AitLocationHeaderCell {
  /** Location referring to a header cell. */
  position: AitTablePosition.headerCell;
  /** This location is in the header section. */
  tableSection: AitRowType.header;
  /** Header row group index. */
  rowGroup: 0;
  /** Identifier for the header row group. */
  rowGroupId: string;
  /** Row index containing the cell. */
  row: number;
  /** Identifier for the row. */
  rowId: string;
  /** Header cells do not have repeats. */
  rowRepeat: undefined;
  /** Cell type for header cells. */
  cellType: AitCellType.header;
  /** Unique cell identifier. */
  cellId: string;
  /** Column index for this cell. */
  column: number;
  /** Optional column repeat id. */
  colRepeat: string | undefined;
  /** When true, this cell is part of a repeated structure. */
  isRepeat: boolean;
  /** Original column index before any processing. */
  originalColumn: number;
}

/**
 * Body location
 */
export interface AitLocationBody extends AitLocationBase {
  /** Location referring to the table body section. */
  position: AitTablePosition.body;
  /** This location is in the body section. */
  tableSection: AitRowType.body;
}

/**
 * Body row group location
 */
export interface AitLocationBodyRowGroup extends AitLocationBase {
  /** Location referring to a body row group. */
  position: AitTablePosition.bodyRowGroup;
  /** This location is in the body section. */
  tableSection: AitRowType.body;
  /** Index of the row group in the body. */
  rowGroup: number;
  /** Identifier for the row group. */
  rowGroupId: string;
}

/**
 * Body row location
 */
export interface AitLocationBodyRow extends AitLocationBase {
  /** Location referring to a specific body row. */
  position: AitTablePosition.bodyRow;
  /** This location is in the body section. */
  tableSection: AitRowType.body;
  /** Index of the row group containing the row. */
  rowGroup: number;
  /** Identifier for the row group. */
  rowGroupId: string;
  /** Row index within the group. */
  row: number;
  /** Row identifier. */
  rowId: string;
  /** Repeat id if this row is a repeat, otherwise undefined. */
  rowRepeat: string | undefined;
  /** Whether this row is a repeat. */
  isRepeat: boolean;
}

/**
 * Body cell location
 */
export interface AitLocationBodyCell extends AitLocationBase {
  /** Location referring to a specific cell in the body. */
  position: AitTablePosition.bodyCell;
  /** This location is in the body section. */
  tableSection: AitRowType.body;
  /** Index of the row group containing the cell. */
  rowGroup: number;
  /** Identifier for the row group. */
  rowGroupId: string;
  /** Row index within the group. */
  row: number;
  /** Row identifier. */
  rowId: string;
  /** Repeat id if this cell is part of a repeated row, otherwise undefined. */
  rowRepeat: string | undefined;
  /** The cell type (row header or body). */
  cellType: AitCellType.rowHeader | AitCellType.body;
  /** Unique identifier for the cell. */
  cellId: string;
  /** Optional column repeat id. */
  colRepeat: string | undefined;
  /** Column index for the cell. */
  column: number;
  /** Whether the cell is part of a repeat. */
  isRepeat: boolean;
  /** Original column index before processing. */
  originalColumn: number;
}

/**
 * Column location
 */
export interface AitLocationColumn extends AitLocationBase {
  /** Location referring to a column header or column metadata. */
  position: AitTablePosition.column;
  /** Identifier for the column. */
  columnId: string;
  /** Original column index before any processing. */
  originalColumn: number;
}

/**
 * Location type for the entire table.
 */
export type AitLocation =
  | AitLocationHeader
  | AitLocationTable
  | AitLocationHeaderRow
  | AitLocationHeaderCell
  | AitLocationBody
  | AitLocationBodyRowGroup
  | AitLocationBodyRow
  | AitLocationBodyCell
  | AitLocationColumn;

/**
 * Column repeat information
 */
export interface AitColumnRepeat {
  /** Index of the column being repeated. */
  columnIndex: number;
  /** Optional repeat id for the column. */
  colRepeat?: string;
  /** True when this column is a repeat. */
  isRepeat?: boolean;
}

export enum AitCellType {
  header = "header",
  rowHeader = "rowHeader",
  body = "body",
}

export enum AitRowType {
  header = "header",
  body = "body",
  bodyRowGroup = "bodyRowGroup",
}

export enum GridItemChangeType {
  deleted = "deleted",
  added = "added",
}

/**
 * Base state for a table stored in the editor or component state.
 *
 * @template T The data type used for cell text and comments.
 */
export interface AitTableStateBase<T> {
  /** Unique id for the table state instance. */
  id: string;
  /** External single-value replacements available to the table. */
  externalSingles: AioExternalSingle<T>[];
  /** External list replacements available to the table. */
  externalLists: AioExternalReplacements<T>[];
  /** When true, show borders around table cells. */
  showCellBorders: boolean;
  /** Column width modifier used for scaling. */
  colWidthMod: number;
  /** Default width applied to cells when none provided. */
  defaultCellWidth: number;
  /** Templates for row groups used when adding new groups. */
  groupTemplates: AitRowGroupData<T>[];
  /** The original table data before edits. */
  originalTableData: AitTableData<T>;
  /** When true, the table is editable. */
  editable: boolean;
  /** When true, traffic lights are disabled. */
  noTrafficLights: boolean;
  /** When true, table-level options are disabled. */
  noTableOptions: boolean;
  /** Editor props used by the embedded editor. */
  editorProps: EditorDefns<T>;
  /** The tlf view mode configuration. */
  tlfViewMode: TlfViewModeConfig;
}

export interface AitTableState<T> extends AitTableStateBase<T> {
  /** Table data after processing (e.g., repeats resolved). */
  processedTableData: AitProcessedTableData<T>;
  /** Locations for which an options window is currently shown. */
  showOptionsWindow: AitLocation[];
  /** Whether there are unsaved changes. */
  changed: boolean;
}

export const ADD_COLUMN = "ADD_COLUMN";
export const ADD_COLUMN_SPAN = "ADD_COLUMN_SPAN";
export const ADD_NEW_HEADER = "ADD_NEW_HEADER";
export const ADD_ROW = "ADD_ROW";
export const ADD_ROW_GROUP = "ADD_ROW_GROUP";
export const ADD_ROW_SPAN = "ADD_ROW_SPAN";
export const DUPLICATE_COLUMN = "DUPLICATE_COLUMN";
export const DUPLICATE_ROW = "DUPLICATE_ROW";
export const DUPLICATE_ROW_GROUP = "DUPLICATE_ROW_GROUP";
export const HIDE_OPTIONS_WINDOW = "HIDE_OPTIONS_WINDOW";
export const LOAD_STATE = "LOAD_STATE";
export const REMOVE_COLUMN = "REMOVE_COLUMN";
export const REMOVE_COLUMN_SPAN = "REMOVE_COLUMN_SPAN";
export const REMOVE_ROW = "REMOVE_ROW";
export const REMOVE_ROW_GROUP = "REMOVE_ROW_GROUP";
export const REMOVE_ROW_SPAN = "REMOVE_ROW_SPAN";
export const SET_DECIMAL_ALIGN_PERCENT = "SET_DECIMAL_ALIGN_PERCENT";
export const SET_REPLACEMENTS = "SET_REPLACEMENTS";
export const SET_ROW_HEADER_COLUMNS = "SET_ROW_HEADER_COLUMNS";
export const SET_SPACE_AFTER = "SET_SPACE_AFTER";
export const SET_TRAFFIC_LIGHTS = "SET_TRAFFIC_LIGHTS";
export const SHOW_OPTIONS_WINDOW = "SHOW_OPTIONS_WINDOW";
export const TABLE_CHANGE_PUSHED = "TABLE_CHANGE_PUSHED";
export const UPDATE_TABLE_CELL = "UPDATE_TABLE_CELL";
export const UPDATE_COLUMN_WIDTH = "UPDATE_COLUMN_WIDTH";
export const UPDATE_COMMENTS = "UPDATE_COMMENTS";
export const UPDATE_HEADER_CELL = "UPDATE_HEADER_CELL";

interface AitTableActionAddColumn {
  operation: typeof ADD_COLUMN | typeof DUPLICATE_COLUMN;
  columnIndex: number;
  newColumnLength: number;
}

interface AitTableActionAddRowGroup {
  operation: typeof ADD_ROW_GROUP;
  location: AitLocationBodyRowGroup;
  newLength: number;
  templateName?: string;
}

interface AitTableActionBase {
  operation: typeof ADD_NEW_HEADER | typeof TABLE_CHANGE_PUSHED;
}

interface AitTableActionBool {
  operation: typeof SET_TRAFFIC_LIGHTS;
  bool: boolean;
}

interface AitTableActionCells {
  operation: typeof REMOVE_COLUMN;
  cellIds: string[];
  value: number;
}

interface AitTableActionHeaderCell {
  operation:
    | typeof ADD_COLUMN_SPAN
    | typeof ADD_ROW_SPAN
    | typeof REMOVE_COLUMN_SPAN
    | typeof REMOVE_ROW_SPAN;
  location: AitLocationHeaderCell;
  newSpan: number;
}

interface AitTableActionLoad<T> {
  operation: typeof LOAD_STATE;
  newState: AitTableStateBase<T>;
}

interface AitTableActionLocation {
  operation: typeof HIDE_OPTIONS_WINDOW | typeof SHOW_OPTIONS_WINDOW;
  location: AitLocation;
}

interface AitTableActionReplacement<T> {
  operation: typeof SET_REPLACEMENTS;
  replacements: AioReplacement<T>[];
  location: AitLocationHeader | AitLocationBodyRowGroup;
}

interface AitTableActionRow {
  operation: typeof DUPLICATE_ROW | typeof REMOVE_ROW;
  location: AitLocationHeaderRow | AitLocationBodyRow;
  newLength: number;
}

interface AitTableActionRowGroup {
  operation: typeof DUPLICATE_ROW_GROUP | typeof REMOVE_ROW_GROUP;
  location: AitLocationBodyRowGroup;
  newLength: number;
}

interface AitTableActionRowOrGroup {
  operation: typeof ADD_ROW;
  location: AitLocationHeader | AitLocationHeaderRow | AitLocationBodyRow | AitLocationBodyRowGroup;
  newLength: number;
}

interface AitTableActionSpaceAfter {
  operation: typeof SET_SPACE_AFTER;
  location: AitLocationBodyRowGroup;
  bool: boolean;
}

interface AitTableActionUpdateBodyCell<T> {
  operation: typeof UPDATE_TABLE_CELL;
  location: AitLocationBodyCell;
  cellData: Partial<AitCellData<T>>;
}

interface AitTableActionUpdateColumnWidth {
  operation: typeof UPDATE_COLUMN_WIDTH;
  location: AitLocationHeaderCell;
  value: number;
}

export interface AitTableActionUpdateComments<T> {
  operation: typeof UPDATE_COMMENTS;
  location:
    | AitLocationTable
    | AitLocationHeader
    | AitLocationHeaderCell
    | AitLocationBodyRowGroup
    | AitLocationBodyCell;
  comment: T;
}

interface AitTableActionUpdateHeaderCell<T> {
  operation: typeof UPDATE_HEADER_CELL;
  location: AitLocationHeaderCell;
  cellData: Partial<AitHeaderCellData<T>>;
}

interface AitTableActionValue {
  operation: typeof SET_DECIMAL_ALIGN_PERCENT | typeof SET_ROW_HEADER_COLUMNS;
  value: number;
}

export type AitTableAction<T> =
  | AitTableActionAddColumn
  | AitTableActionAddRowGroup
  | AitTableActionBase
  | AitTableActionBool
  | AitTableActionCells
  | AitTableActionHeaderCell
  | AitTableActionLoad<T>
  | AitTableActionLocation
  | AitTableActionReplacement<T>
  | AitTableActionRow
  | AitTableActionRowGroup
  | AitTableActionRowOrGroup
  | AitTableActionSpaceAfter
  | AitTableActionUpdateBodyCell<T>
  | AitTableActionUpdateColumnWidth
  | AitTableActionUpdateComments<T>
  | AitTableActionUpdateHeaderCell<T>
  | AitTableActionValue;
